/**
 * Sales — one flow with stages, where there used to be four screens.
 *
 * **What this replaces, and why.** Selling a unit to somebody was told across Reservations, then
 * Purchases, then Payments, then a handover button, and the reader carried the thread between them.
 * Five of the fifteen rail items were doors onto that one job. The operator's verdict was *"I have a
 * whole lot of stuff that I can't even tell what is what"*, and item 5 of
 * `14-DEVELOPMENT-MODULE-PLAN.md` settles the answer: **one list, one record per sale, and the stage
 * on the row.**
 *
 * **A reservation is a sale that has not converted.** It is not a second kind of thing on this
 * screen; it is the first stage of the same thing. The two records stay separate on the server for a
 * good reason — a reservation has no price and no schedule, and giving it empty ones to fit a shared
 * table is how a list starts lying — and they are merged here, where the only thing being shared is
 * a row. `lib/sales.ts` holds that merge and the stage derivation, and holds them **once**, because
 * the Overview counts with the same functions this list filters with. A figure that counted one way
 * and a list that filtered another is a dashboard nobody believes twice.
 *
 * **The chips are the summary as well as the filter.** Each carries its count, the reader never
 * leaves the page, and every chip has an address — which is what lets an Overview figure link
 * straight to the rows behind it. Item 12 makes that a rule rather than a nicety.
 *
 * **Why the whole list is fetched and then narrowed here.** Stage is derived rather than stored, so
 * there is no column for the server to filter on; asking it for "the reserved ones" would mean
 * teaching two endpoints a rule this file already knows, and the two would drift. Two hundred is the
 * server's own ceiling and is "all of them" for any book this control makes sense on; past that the
 * pager below works on what came back, and the counts say so.
 */

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { titleCase, useFormat, useToday } from "@/lib/format";
import { useSoldNoun } from "@/lib/development";
import {
  SALE_STAGES,
  STAGE_LABEL,
  STAGE_TONE,
  countByStage,
  filterFromParams,
  filterSales,
  mergeSales,
  salesLink,
  type PurchaseRow,
  type ReservationRow,
  type SaleRow,
  type SaleStage,
} from "@/lib/sales";
import { Chips, type ChipSpec } from "@/components/chips";
import { Badge, Card, EmptyState, ErrorState, LinkButton, PageHeader, Spinner, Table } from "@/components/ui";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, useLocalPage } from "@/lib/list-params";

/**
 * Everything the module is selling, both records, in one hook.
 *
 * Shared with the Overview and with a buyer's page, so all three read the same rows through the same
 * derivation. `staleTime` is short rather than zero: this is three screens' worth of data and a
 * developer moves between them constantly, but a payment recorded on a sale has to show on the list
 * behind it, so the mutations that record one invalidate `["sales"]`.
 */
export function useSales() {
  const purchases = useQuery({
    queryKey: ["sales", "purchases"],
    queryFn: () => api.get<{ purchases: PurchaseRow[]; page: { total: number } }>("/purchases?perPage=200"),
  });
  const reservations = useQuery({
    queryKey: ["sales", "reservations"],
    queryFn: () => api.get<{ reservations: ReservationRow[]; page: { total: number } }>("/reservations?perPage=200"),
  });

  const rows = useMemo(
    () => mergeSales(purchases.data?.purchases ?? [], reservations.data?.reservations ?? []),
    [purchases.data, reservations.data],
  );

  return {
    rows,
    isLoading: purchases.isLoading || reservations.isLoading,
    error: purchases.error ?? reservations.error,
    refetch: () => {
      void purchases.refetch();
      void reservations.refetch();
    },
    /*
      Whether either list was cut off at the server's ceiling.

      Said rather than hidden. A count that quietly means "of the two hundred we looked at" is the
      one thing a figure on a dashboard must never do, so the screen carries the caveat when it
      applies — which is never, on any book this product has yet seen.
    */
    truncated:
      (purchases.data?.page.total ?? 0) > (purchases.data?.purchases.length ?? 0) ||
      (reservations.data?.page.total ?? 0) > (reservations.data?.reservations.length ?? 0),
  };
}

/** What a row's stage looks like on the row. */
export function StageBadge({ row }: { row: SaleRow }) {
  return (
    <span className="flex flex-col items-start gap-0.5">
      <Badge tone={STAGE_TONE[row.stage]}>{STAGE_LABEL[row.stage]}</Badge>
      {row.stageDetail ? <span className="text-xs text-ink-500">{row.stageDetail}</span> : null}
    </span>
  );
}

export function SalesPage() {
  const [params] = useSearchParams();
  const list = useListParams();
  const fmt = useFormat();
  const today = useToday();
  const noun = useSoldNoun();
  const { rows, isLoading, error, refetch, truncated } = useSales();

  const filter = filterFromParams(params);

  /*
    The search box narrows what the chips then count.

    That order is deliberate and is the one a reader expects: searching for a buyer's name and then
    reading "2 in payment" should mean two of *theirs*, not two in the whole book. The chips are a
    summary of what is on the screen.
  */
  const searched = useMemo(() => {
    const text = (list.q ?? "").trim().toLowerCase();
    if (!text) return rows;
    return rows.filter(
      (row) =>
        row.buyerName.toLowerCase().includes(text) ||
        row.reference.toLowerCase().includes(text) ||
        row.estateName.toLowerCase().includes(text) ||
        (row.unitLabel ?? "").toLowerCase().includes(text),
    );
  }, [rows, list.q]);

  /* The narrowings that are not a stage — overdue, awaiting allocation, and the rest — apply
     underneath the chips, so a chip's count is the count of what pressing it would show. */
  const scoped = useMemo(
    () => filterSales(searched, { ...filter, stage: null }, today),
    [
      searched,
      filter.sold,
      filter.overdue,
      filter.unallocated,
      filter.awaitingHandover,
      filter.lapsing,
      filter.estateId,
      filter.buyerId,
      today,
    ],
  );

  const counts = useMemo(() => countByStage(scoped), [scoped]);
  const shown = useMemo(
    () => (filter.stage ? scoped.filter((row) => row.stage === filter.stage) : scoped),
    [scoped, filter.stage],
  );

  const page = useLocalPage(shown, 20);

  /*
    The chips.

    The operator named six stages and "All". `Cancelled` is drawn **only when there is one**, and
    that is the rule that keeps the strip honest in both directions: All has to equal the sum of the
    chips or the numbers stop meaning anything, and a withdrawn sale must not become unreachable
    just because it is rare. On the ordinary book the strip is exactly the seven that were asked
    for.
  */
  const chips: ChipSpec[] = [
    { key: "all", label: "All", to: salesLink({ ...filter, stage: null }), count: scoped.length },
    ...SALE_STAGES.filter((stage) => stage !== "cancelled" || counts.cancelled > 0).map((stage) => ({
      key: stage,
      label: STAGE_LABEL[stage],
      to: salesLink({ ...filter, stage }),
      count: counts[stage],
      /* A dot and no spoken label: unlike a phase, a stage's own name is the chip's label, so a
         `toneLabel` would read the same word twice to a screen reader. */
      tone: STAGE_TONE[stage],
    })),
  ];

  /* What else is narrowing the list, said in words with a way out. A filter arrived at from an
     Overview figure is invisible otherwise, and "why are there only four?" is the result. */
  const narrowings: Array<{ label: string; without: string }> = [];
  if (filter.sold) narrowings.push({ label: "Sales only, no holds", without: salesLink({ ...filter, sold: false }) });
  if (filter.overdue) narrowings.push({ label: "In arrears", without: salesLink({ ...filter, overdue: false }) });
  if (filter.unallocated)
    narrowings.push({ label: `Awaiting a ${noun.one}`, without: salesLink({ ...filter, unallocated: false }) });
  if (filter.awaitingHandover)
    narrowings.push({ label: "Awaiting handover", without: salesLink({ ...filter, awaitingHandover: false }) });
  if (filter.lapsing) narrowings.push({ label: "Holds running out", without: salesLink({ ...filter, lapsing: false }) });
  if (filter.estateId) {
    const named = rows.find((row) => row.estateId === filter.estateId);
    narrowings.push({ label: named?.estateName ?? "One property", without: salesLink({ ...filter, estateId: null }) });
  }
  if (filter.buyerId) {
    const named = rows.find((row) => row.buyerId === filter.buyerId);
    narrowings.push({ label: named?.buyerName ?? "One buyer", without: salesLink({ ...filter, buyerId: null }) });
  }

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle={`${scoped.length} ${scoped.length === 1 ? "sale" : "sales"}`}
        description="Every sale from the hold that started it to the day the keys were handed over. A reservation is a sale that has not converted."
        actions={
          <LinkButton to="/app/development/sales/new" need="lease.edit">
            Record a sale
          </LinkButton>
        }
      />

      <Chips chips={chips} current={filter.stage ?? "all"} label="Stage" />

      {narrowings.length ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-ink-600">
          <span className="font-medium">Also narrowed to:</span>
          {narrowings.map((narrowing) => (
            <Link
              key={narrowing.label}
              to={narrowing.without}
              className="rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-ink-700 hover:border-ink-300"
            >
              {narrowing.label}
              <span className="ml-1.5 text-ink-400" aria-hidden>
                ✕
              </span>
              <span className="sr-only">— remove this filter</span>
            </Link>
          ))}
        </div>
      ) : null}

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-3">
          <div className="min-w-[16rem] flex-1">
            <SearchBox params={list} placeholder="Search by buyer, property or reference…" />
          </div>
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={refetch} />
        ) : !shown.length ? (
          <EmptyState
            title={list.q ? `No sales matched “${list.q}”.` : "No sales at this stage."}
            hint="A sale is recorded from the sales desk, or created by converting a reservation."
            action={
              <LinkButton to="/app/development/sales/new" need="lease.edit">
                Record a sale
              </LinkButton>
            }
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <th>Buyer</th>
                  <th>Property</th>
                  <th>{titleCase(noun.one)}</th>
                  <th>Stage</th>
                  <th>Next payment</th>
                  <th className="num">Balance outstanding</th>
                </tr>
              }
            >
              {page.rows.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <td>
                    <Link className="font-medium text-brand-700 hover:underline" to={row.href}>
                      {row.buyerName}
                    </Link>
                    <div className="text-xs text-ink-500">{row.reference}</div>
                  </td>
                  <td className="text-ink-600">{row.estateName}</td>
                  <td className="text-ink-600">
                    {/* "Not yet allocated", never an empty box. Allocation is a stage of the sale
                        and the ordinary state for months, so the cell says so in words rather than
                        leaving a dash the reader has to interpret. */}
                    {row.unitLabel ?? <span className="text-ink-400">Not yet allocated</span>}
                  </td>
                  <td>
                    <StageBadge row={row} />
                  </td>
                  <td className="text-ink-600">
                    {/*
                      The amount **and** the date, on the operator's instruction to show both: the
                      figure you chase and the figure you are owed are different questions, and a
                      row that answers only one sends somebody to a second screen.

                      On a reservation the next thing that happens is the hold running out, which
                      has a date and no amount. On a sale the amount is not yet on the list
                      endpoint — see `PurchaseRow.nextDueDate` — so the date carries the row until
                      it is.
                    */}
                    {row.nextDueDate ? (
                      <>
                        {row.nextDueAmount !== null ? (
                          <div className="tabular font-medium">{fmt.money(row.nextDueAmount)}</div>
                        ) : null}
                        <div className={row.nextDueAmount !== null ? "text-xs text-ink-500" : undefined}>
                          {fmt.date(row.nextDueDate)}
                          {row.kind === "reservation" ? (
                            <span className="text-ink-400"> · hold ends</span>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="num">
                    {row.price === null ? (
                      <span className="text-ink-400">—</span>
                    ) : (
                      <>
                        <div className={`tabular font-medium ${row.arrears > 0 ? "text-danger-600" : ""}`}>
                          {fmt.money(row.outstanding)}
                        </div>
                        {row.arrears > 0 ? (
                          <div className="text-xs text-danger-600">{fmt.money(row.arrears)} overdue</div>
                        ) : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pagination meta={page.meta} params={page.params} noun="sales" />
          </>
        )}
      </Card>

      {truncated ? (
        <p className="mt-3 text-xs text-ink-500">
          The counts above cover the most recent two hundred records. Narrow the list to see the rest.
        </p>
      ) : null}
    </div>
  );
}
